import React, { useState, useEffect } from "react";
import { Table, Button, Modal, Input, Space, message, Popconfirm, ConfigProvider } from 'antd';
import { SearchOutlined, EditOutlined, DeleteOutlined, UserAddOutlined } from '@ant-design/icons';
import { searchUsers, deleteUser } from "../../utils/api/user";
import CustomToast from "../../components/CustomToast";
import 'antd/dist/reset.css';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

function UserManageWithAction() {
  const navigate = useNavigate();
  const [details, setMessage] = useState({ message: "", type: "" });
  const [showToast, setShowToast] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  
  const [searchData, setSearchData] = useState({
    username: "",
  });
  const [width, setWidth] = useState(window.innerWidth);

  const columns = [
    {
      title: <span className="text-white py-1 block">ROLE</span>,
      dataIndex: 'role',
      key: 'role',
      width: 150,
      align: 'center',
      sorter: (a, b) => a.role?.localeCompare(b.role),
      render: (text) => text || '-',
    },
    {
      title: <span className="text-white py-1 block">FIRST NAME</span>,
      dataIndex: 'first_name',
      key: 'first_name',
      width: 150,
      align: 'center',
      sorter: (a, b) => (a.first_name || '').localeCompare(b.first_name || ''),
      render: (text) => text || '-',
    },
    {
      title: <span className="text-white py-1 block">LAST NAME</span>,
      dataIndex: 'last_name',
      key: 'last_name',
      width: 150,
      align: 'center',
      sorter: (a, b) => (a.last_name || '').localeCompare(b.last_name || ''),
      render: (text) => text || '-',
    },
    {
      title: <span className="text-white py-1 block">USERNAME</span>,
      dataIndex: 'username',
      key: 'username',
      width: 150,
      align: 'center',
      sorter: (a, b) => (a.username || '').localeCompare(b.username || ''),
      render: (text) => text || '-',
    },
    {
      title: <span className="text-white py-1 block">AREA CODE</span>,
      dataIndex: 'area_code',
      key: 'area_code',
      width: 120,
      align: 'center',
      sorter: (a, b) => (a.area_code || '').localeCompare(b.area_code || ''),
      render: (text) => text || '-',
    },
    {
      title: <span className="text-white py-1 block">DAILY QUOTA</span>,
      dataIndex: 'daily_quota',
      key: 'daily_quota',
      width: 150,
      align: 'center',
      sorter: (a, b) => (a.daily_quota || 0) - (b.daily_quota || 0),
      render: (text) => text || '0',
    },
    {
      title: <span className="text-white py-1 block">ACTIONS</span>,
      key: 'actions',
      width: 200,
      align: 'center',
      render: (_, record) => (
        <Space size="middle">
          <Button 
            type="primary" 
            icon={<EditOutlined />} 
            onClick={() => navigate(`/user/edit/${record.id}`)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this user?"
            onConfirm={() => handleDeleteUser(record.id)}
            okText="Yes"
            cancelText="No"
            placement="left"
          >
            <Button 
              // danger 
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleDeleteUser = async (userId) => {
    try {
      const response = await deleteUser(userId);
      if (response?.data) {
        message.success('User deleted successfully');
        handleSearch(pagination);
      } else {
        const errorMsg = response?.data?.messages?.error || 'Failed to delete user';
        message.error(errorMsg);
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      message.error('An error occurred while deleting the user');
    }
  };

  const handleSearch = async (pagination = { current: 1, pageSize: 10 }) => {
    try {
      setLoading(true);
      const response = await searchUsers(searchData.username);

      if (response?.data?.data) {
        const formattedData = response.data.data.map(item => ({
          ...item,
          key: item.id || Math.random().toString(36).substr(2, 9),
        }));
        
        setData(formattedData);
        setPagination({
          ...pagination,
          total: response.data.total || formattedData.length,
        });
      } else {
        setData([]);
        const responseMessage = response?.data?.messages?.error;
        setMessage({
          message: responseMessage ?? "No users found",
          type: "warning",
        });
        setShowToast(true);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      const responseMessage = error.response?.data?.messages?.error || 'Something went wrong.';
      setMessage({
        message: responseMessage,
        type: "danger",
      });
      setShowToast(true);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (pagination, filters, sorter) => {
    handleSearch(pagination);
  };

  useEffect(() => {
    handleSearch(pagination);
  }, []);

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#FB9C0C',
          borderRadius: 6,
        },
      }}
    >
      <div className="p-2">
        <div className="text-center">
          <div className="col-12">
            <h1 className="title">USERS</h1>
          </div>
        </div>
        
        <div className="d-flex flex-wrap gap-3 mb-4 align-items-end">
          <div style={{ width: "250px" }}>
            <label style={{ fontWeight: 500, marginBottom: 4, display: 'block' }}>Username</label>
            <Input
              placeholder="Search Username"
              value={searchData.username}
              onChange={(e) => setSearchData({ ...searchData, username: e.target.value })}
            />
          </div>
          
          <div className="d-flex align-items-end">
            <Space>
              <Button type="primary" onClick={() => handleSearch(pagination)}>
                Search
              </Button>
              <Button 
                type="primary" 
                icon={<UserAddOutlined />}
                onClick={() => navigate('/user/add')}
              >
                Add User
              </Button>
            </Space>
          </div>
        </div>
      
      <div className="row">
        <div className="col-12">
          <div style={{ overflowX: 'auto', maxHeight: '70vh', overflowY: 'auto' }}>
            <Table
              // className="no-hover"
              columns={columns}
              dataSource={data}
              rowKey="id"
              pagination={{
                ...pagination,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                showTotal: (total) => `Total ${total} users`,
                onChange: (page, pageSize) => handleSearch(page, pageSize),
              }}
              scroll={{ x: 'max-content' }}
              bordered
              size="small"
              loading={loading}
            />
          </div>
        </div>
      </div>
      
      <CustomToast
        type={details.type}
        message={details.message}
        title="Users"
        show={showToast}
        setShow={setShowToast}
      />
    </div>
    </ConfigProvider>
  );
}

export default UserManageWithAction;
